'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import emptyFunction from 'react-utils/emptyFunction';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIModal from 'UIComponents/dialog/UIModal';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UILink from 'UIComponents/link/UILink';
import UIBreakString from 'UIComponents/text/UIBreakString';
import { FilePropTypes } from 'DesignData/propTypes/finder';
import * as finderSelectors from 'DesignData/selectors/finder';
import * as OptionsActions from 'DesignData/actions/finder/Options';
import * as FilesActions from 'DesignData/actions/finder/Files';
import FileDependentsLink from 'DesignComponents/nav/FileDependentsLink';
import H2 from 'UIComponents/elements/headings/H2';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';

const i18nKey = key => `designComponents.modals.RenameFileConfirmModal.${key}`;

function makeMapStateToProps() {
  const getIsUpgradeableModule = finderSelectors.makeGetIsUpgradeableModule();
  return function mapStateToProps(state, props) {
    const {
      name: fileName
    } = finderSelectors.getFile(state, props);
    return {
      isUpgradeableModule: getIsUpgradeableModule(state, props),
      fileName
    };
  };
}

const mapDispatchToProps = {
  hideRenameConfirm: OptionsActions.hideRenameFileConfirm,
  renameFile: FilesActions.renameFile
};

class RenameFileConfirmModal extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      confirmed: false,
      dismiss: false
    };

    this.handleCancel = () => {
      this.props.callback(false);
    };

    this.handleConfirm = () => {
      const {
        callback,
        hideRenameConfirm,
        newFileName,
        renameFile,
        fileId
      } = this.props;

      if (this.state.dismiss) {
        hideRenameConfirm();
      }

      renameFile(fileId, newFileName);
      callback(true);
    };

    this.handleConfirmChange = evt => {
      this.setState({
        confirmed: evt.target.checked
      });
    };

    this.handleDependentsClick = () => {
      this.props.callback(false);
    };

    this.handleDismissChange = evt => {
      this.setState({
        dismiss: evt.target.checked
      });
    };
  }

  render() {
    const {
      fileId,
      fileName,
      newFileName,
      isUpgradeableModule
    } = this.props;
    const {
      confirmed,
      dismiss
    } = this.state;
    const warningText = isUpgradeableModule ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: i18nKey('warning.legacyModuleText')
    }) : /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: i18nKey('warning.text'),
      options: {
        newFileName,
        learnMoreLink: /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: "http://designers.hubspot.com/docs/hubl/intro-to-hubl#including-files-in-files",
          use: "on-dark",
          children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
            message: i18nKey('warning.learnMore.text')
          })
        })
      }
    });
    return /*#__PURE__*/_jsxs(UIModal, {
      size: "auto",
      use: "danger",
      width: 660,
      "data-test-id": "rename-file-modal",
      children: [/*#__PURE__*/_jsx(UIDialogHeader, {
        children: /*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(UIBreakString, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('header.text'),
              options: {
                fileName: I18n.SafeString(fileName),
                newFileName: I18n.SafeString(newFileName)
              }
            })
          })
        })
      }), /*#__PURE__*/_jsx(UIDialogBody, {
        children: /*#__PURE__*/_jsx("p", {
          children: /*#__PURE__*/_jsx(UIBreakString, {
            children: warningText
          })
        })
      }), /*#__PURE__*/_jsxs(UIDialogFooter, {
        children: [/*#__PURE__*/_jsxs("div", {
          className: "m-bottom-6",
          children: [/*#__PURE__*/_jsx(UICheckbox, {
            checked: confirmed,
            onChange: this.handleConfirmChange,
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              "data-test-id": "modal__rename-file__confirm-checkbox",
              message: i18nKey('checkboxes.confirm.label'),
              options: {
                dependentsLink: /*#__PURE__*/_jsx(FileDependentsLink, {
                  fileId: fileId,
                  onClick: this.handleDependentsClick,
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: i18nKey('buttons.dependents.text')
                  })
                })
              }
            })
          }), /*#__PURE__*/_jsx(UICheckbox, {
            checked: dismiss,
            disabled: !confirmed,
            onChange: this.handleDismissChange,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('checkboxes.dismiss.label')
            })
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "has--horizontal-spacing m-left-0",
          children: [/*#__PURE__*/_jsx(UIButton, {
            "data-test-id": "modal__rename-file__rename-button",
            disabled: !confirmed,
            onClick: this.handleConfirm,
            use: "danger",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('buttons.confirm.text')
            })
          }), /*#__PURE__*/_jsx(UIButton, {
            onClick: this.handleCancel,
            use: "tertiary-light",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('buttons.cancel.text')
            })
          })]
        })]
      })]
    });
  }

}

RenameFileConfirmModal.propTypes = {
  callback: PropTypes.func,
  fileId: FilePropTypes.fileId.isRequired,
  newFileName: FilePropTypes.fileName.isRequired,
  // @connect
  fileName: FilePropTypes.fileName.isRequired,
  hideRenameConfirm: PropTypes.func.isRequired,
  isUpgradeableModule: PropTypes.bool.isRequired,
  renameFile: PropTypes.func.isRequired
};
RenameFileConfirmModal.defaultProps = {
  callback: emptyFunction
};
export default connect(makeMapStateToProps, mapDispatchToProps)(RenameFileConfirmModal);